import { formatOrderDayLabel } from "../../functions/src/api/dates";
import type { FundingRecord } from "../../functions/src/types";
import type { Language } from "./langConfig";
import type { WalletHistoryStrings } from "../Order/profile/wallet-history-strings";
import { formatFundActivityDetail } from "../Order/profile/formatFundActivityLabel";

export type WalletHistoryLedgerRow = {
    key: string;
    entry: FundingRecord;
};

export type WalletHistoryDisplayRow = {
    id: string;
    amount: number;
    balance: number;
    timestamp: number;
    schoolKey?: string;
    detail?: string;
    /** Single ledger row — use standard activity formatting. */
    entry?: FundingRecord;
};

function isBasketCheckoutLedgerEntry(entry: FundingRecord): boolean {
    const type = entry.activity?.type;
    return type === "placeOrder" || type === "changeOrderRefund";
}

function checkoutSessionAt(entry: FundingRecord): number | undefined {
    const activity = entry.activity;
    if (activity?.type === "placeOrder" || activity?.type === "changeOrderRefund") {
        return activity.checkoutSessionAt;
    }
    return undefined;
}

function orderDateFromEntry(entry: FundingRecord): string | undefined {
    const activity = entry.activity;
    if (activity?.type === "placeOrder" || activity?.type === "changeOrderRefund") {
        return activity.orderDate;
    }
    return undefined;
}

function schoolKeyFromEntry(entry: FundingRecord): string | undefined {
    return entry.activity?.schoolKey || entry.schoolKey;
}

function roundEuro(amount: number): number {
    return Math.round(amount * 100) / 100;
}

function ledgerRowToDisplayRow(
    { key, entry }: WalletHistoryLedgerRow,
    strings: WalletHistoryStrings,
    lang: Language,
): WalletHistoryDisplayRow {
    return {
        id: key,
        amount: entry.amount,
        balance: entry.balance,
        timestamp: entry.timestamp,
        schoolKey: schoolKeyFromEntry(entry),
        detail: entry.activity ? formatFundActivityDetail(entry.activity, strings, lang) : strings.legacyBalanceChange,
        entry,
    };
}

function formatGroupedCheckoutDetail(
    entries: WalletHistoryLedgerRow[],
    strings: WalletHistoryStrings,
    lang: Language,
): string | undefined {
    const orderDates = [...new Set(entries.map(({ entry }) => orderDateFromEntry(entry)).filter(Boolean) as string[])]
        .sort((a, b) => b.localeCompare(a));
    if (!orderDates.length) return undefined;

    const labels = orderDates.map((date) => formatOrderDayLabel(date, lang));
    const hasChangeRefund = entries.some(({ entry }) => entry.activity?.type === "changeOrderRefund");

    if (orderDates.length === 1 && hasChangeRefund) {
        return strings.changeOrderRefundDetail(labels[0]);
    }
    if (orderDates.length === 1) {
        return strings.orderForDate(labels[0]);
    }
    return strings.orderForDates(labels);
}

function makeCheckoutGroupDisplayRow(
    sessionAt: number,
    entries: WalletHistoryLedgerRow[],
    strings: WalletHistoryStrings,
    lang: Language,
): WalletHistoryDisplayRow {
    const sorted = [...entries].sort((a, b) => (a.entry.timestamp || 0) - (b.entry.timestamp || 0));
    const last = sorted[sorted.length - 1].entry;
    const amount = roundEuro(sorted.reduce((sum, { entry }) => sum + (entry.amount || 0), 0));
    return {
        id: `checkout:${sessionAt}`,
        amount,
        balance: last.balance,
        timestamp: last.timestamp,
        schoolKey: schoolKeyFromEntry(last),
        detail: formatGroupedCheckoutDetail(sorted, strings, lang),
    };
}

/** One parent-facing row per basket checkout (shared checkoutSessionAt). Legacy single-day rows pass through unchanged. */
export function groupWalletHistoryRows(
    rows: WalletHistoryLedgerRow[],
    strings: WalletHistoryStrings,
    lang: Language,
): WalletHistoryDisplayRow[] {
    const bySession = new Map<number, WalletHistoryLedgerRow[]>();
    const passthrough: WalletHistoryLedgerRow[] = [];

    for (const row of rows) {
        const sessionAt = checkoutSessionAt(row.entry);
        if (sessionAt != null && isBasketCheckoutLedgerEntry(row.entry)) {
            const list = bySession.get(sessionAt) ?? [];
            list.push(row);
            bySession.set(sessionAt, list);
        } else {
            passthrough.push(row);
        }
    }

    const displayRows = passthrough.map((row) => ledgerRowToDisplayRow(row, strings, lang));
    for (const [sessionAt, entries] of bySession) {
        if (entries.length === 1) {
            displayRows.push(ledgerRowToDisplayRow(entries[0], strings, lang));
        } else {
            displayRows.push(makeCheckoutGroupDisplayRow(sessionAt, entries, strings, lang));
        }
    }

    displayRows.sort((a, b) => b.timestamp - a.timestamp);
    return displayRows;
}
